import React, { useState, Component } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { useStore, levelForPoints } from '../lib/store';
import type { RegisteredUser } from '../lib/store';
import { InstagramFollow } from './InstagramFollow';
import { ChevronRight, Check, UserPlus, RefreshCw, Calendar, CalendarPlus, Download, Copy, Clock, MapPin, Sparkles, Briefcase, Home as HomeIcon, Package, Star, Bell, ArrowRight } from 'lucide-react';
import { googleCalendarUrl, downloadIcs, dropDetailsText } from '../lib/calendar';
type Step = 'entry' | 'first-basics' | 'returning-id' | 'catch2' | 'catch3' | 'catch-recurring' | 'done';
interface RegistrationFormProps {
  onSuccess?: () => void;
  isWalkIn?: boolean;
}
export function RegistrationForm({
  onSuccess,
  isWalkIn = false
}: RegistrationFormProps) {
  const [step, setStep] = useState<Step>('entry');
  // Self-selected path on the entry screen — the source of truth for
  // new-vs-returning (phone matching alone was unreliable).
  const [flow, setFlow] = useState<'first' | 'returning'>('first');
  const [isLoading, setIsLoading] = useState(false);
  // Identity + progressively-collected profile fields.
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [occupation, setOccupation] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [profession, setProfession] = useState('');
  const [city, setCity] = useState('');
  const [workArea, setWorkArea] = useState('');
  const [favoriteArea, setFavoriteArea] = useState('');
  const [pickupPref, setPickupPref] = useState('');
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>({});
  // Opt-in to be kept up to date about new drops (app + Instagram, NOT SMS).
  const [notifyOptIn, setNotifyOptIn] = useState(true);
  // Which catch number this submission represents (1 = first catch).
  const [catchNumber, setCatchNumber] = useState(1);
  // First-time onboarding: 3 slides shown only after a user's FIRST catch.
  // Returning users skip straight to the success screen.
  const [onboardingSlide, setOnboardingSlide] = useState(0);
  const [recognized, setRecognized] = useState<RegisteredUser | null>(null);
  const [detailsCopied, setDetailsCopied] = useState(false);
  const [error, setError] = useState('');
  // Capture referral code from URL
  const [invitedBy] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('ref') || undefined;
  });
  // Capture partner code from URL or manual input
  const [partnerCode, setPartnerCode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('partner') || '';
  });
  const {
    setUser,
    addReservation,
    activeEventId,
    registeredUsers,
    events,
    reservations,
    recurringQuestions,
    hydrateUserByPhone,
    setNotificationOptIn
  } = useStore();
  // The "next drop" to invite friends to.
  const nextDrop = [...events].filter(e => e.id !== activeEventId).sort((a, b) => a.date.localeCompare(b.date)).find(e => e.date >= new Date().toISOString().slice(0, 10)) || [...events].filter(e => e.id !== activeEventId).sort((a, b) => a.date.localeCompare(b.date))[0] || null;
  const mapsUrl = (address: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  // Copy the full drop details so the user can paste them into any calendar.
  const handleCopyDetails = async () => {
    if (!nextDrop) return;
    try {
      await navigator.clipboard.writeText(dropDetailsText(nextDrop));
      setDetailsCopied(true);
      setTimeout(() => setDetailsCopied(false), 2500);
    } catch {
      setDetailsCopied(false);
    }
  };
  // --- OPTION SETS ---
  const ageOptions = ['Under 18', '18–24', '25–34', '35–44', '45+'];
  // Broadened so it defines a person better, without being overwhelming.
  const occupationOptions = ['Student', 'Employed full-time', 'Employed part-time', 'Self-employed', 'Job seeking', 'Parental leave', 'Retired', 'Other'];
  const interestOptions = [{
    label: 'Snacks & candy',
    emoji: '🍿'
  }, {
    label: 'Drinks',
    emoji: '🥤'
  }, {
    label: 'Health & fitness',
    emoji: '💪'
  }, {
    label: 'Beauty',
    emoji: '✨'
  }, {
    label: 'Tech',
    emoji: '📱'
  }, {
    label: 'Fashion',
    emoji: '👕'
  }, {
    label: 'Experiences',
    emoji: '🎉'
  }, {
    label: 'Food',
    emoji: '🍕'
  }];
  // Stockholm areas people like to hang out in.
  const areaOptions = ['Södermalm', 'Östermalm', 'Norrmalm', 'Vasastan', 'Kungsholmen', 'Gamla Stan', 'Djurgården', 'Other'];
  const pickupOptions = ['Instabox', 'Budbee', 'PostNord Box', 'Bring', 'Normal', 'ICA', 'Coop', 'Willys', 'Pressbyrån', 'Hemköp'];
  const toggleInterest = (interest: string) => {
    if (interests.includes(interest)) {
      setInterests(interests.filter(i => i !== interest));
    } else if (interests.length < 3) {
      setInterests([...interests, interest]);
    }
  };
  const findExisting = () => registeredUsers.find(u => u.phone.replace(/\s/g, '') === phone.replace(/\s/g, ''));
  /**
   * Write everything we know for this person (newly-asked answers take
   * priority, anything we didn't ask is kept from their previous catch) and
   * advance to the celebration screen.
   */
  const completeCatch = async (existing?: RegisteredUser) => {
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 800));
    const fullName = `${firstName} ${lastName}`.trim() || existing?.name || firstName;
    setUser({
      phone,
      name: fullName,
      ageRange: ageRange || existing?.ageRange,
      city: city || existing?.city,
      occupation: occupation || existing?.occupation,
      profession: profession || existing?.profession,
      workArea: workArea || existing?.workArea,
      favoriteArea: favoriteArea || existing?.favoriteArea,
      interests: interests.length ? interests : existing?.interests,
      storePreference: pickupPref || existing?.storePreference,
      customAnswers: Object.keys(customAnswers).length > 0 ? {
        ...(existing?.customAnswers || {}),
        ...customAnswers
      } : existing?.customAnswers,
      invitedBy: existing?.invitedBy || invitedBy,
      partnerCode: existing?.partnerCode || partnerCode,
      points: existing ? existing.points : 0,
      level: existing ? existing.level : levelForPoints(0),
      eligible_for_next_event: existing?.eligible_for_next_event ?? false
    }, flow === 'returning');
    // Persist the new-drop notification preference and, when opting in, ask the
    // browser for permission so we can actually push drop alerts (no SMS).
    setNotificationOptIn(notifyOptIn);
    if (notifyOptIn && typeof Notification !== 'undefined') {
      try {
        if (Notification.permission === 'default') {
          Notification.requestPermission().catch(() => {});
        }
      } catch {
        /* permission API unavailable — opt-in flag is still saved */
      }
    }
    if (isWalkIn && activeEventId) {
      // A drop can only be collected ONCE per person. Skip creating a second
      // redeemed catch if this phone already collected this drop.
      const alreadyCollected = reservations.some(r => r.eventId === activeEventId && r.userId === phone && (r.status === 'redeemed' || r.status === 'reviewed'));
      if (!alreadyCollected) {
        addReservation(activeEventId, 'redeemed');
      }
    }
    setIsLoading(false);
    setStep('done');
  };
  // --- SUBMIT HANDLERS ---
  const handleFirstBasicsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName || !lastName || !phone || !ageRange || !occupation) return;
    setCatchNumber(1);
    completeCatch(undefined);
  };
  const handleReturningIdSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName || !lastName || !phone) return;
    setError('');
    // Restore this person's saved points + activity from the shared sheet so a
    // returning catch builds on their real history (works on any device).
    hydrateUserByPhone(phone);
    const existing = findExisting();
    // Recognised → continue their count. Self-declared returning but unknown
    // → treat as their 2nd catch (matches the store's catch-count logic).
    const n = existing ? existing.catchCount + 1 : 2;
    setCatchNumber(n);
    setRecognized(existing || null);
    // Softly prefill what we already know so we never re-ask it.
    if (existing) {
      setAgeRange(existing.ageRange || '');
      setOccupation(existing.occupation || '');
      setCity(existing.city || '');
      setProfession(existing.profession || '');
      setWorkArea(existing.workArea || '');
      setFavoriteArea(existing.favoriteArea || '');
      setInterests(existing.interests || []);
      setPickupPref(existing.storePreference || '');
    }
    if (n === 2) setStep('catch2');else if (n === 3) setStep('catch3');else {
      // 4th+ catch — check if there are recurring or drop questions
      const activeEvent = events.find(e => e.id === activeEventId);
      const dropQs = activeEvent?.customQuestions || [];
      const recQs = recurringQuestions || [];
      if (dropQs.length > 0 || recQs.length > 0) {
        setStep('catch-recurring');
      } else {
        completeCatch(existing); // 4th+ catch — profile already complete
      }
    }
  };
  const handleCatch2Submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (interests.length === 0 || !profession || !city) return;
    completeCatch(recognized || undefined);
  };
  const handleCatch3Submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!workArea || !favoriteArea || !pickupPref || !city) return;
    completeCatch(recognized || undefined);
  };
  // Small badge that tells returning users this is a fresh set of questions.
  const CatchBadge = ({
    children
  }: {
    children: React.ReactNode;
  }) => <div className="inline-flex items-center bg-brand-lavender text-brand-purple text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-3" data-id="element-497" data-mp="3vjujarhirzzx">
      <Sparkles className="w-3.5 h-3.5 mr-1.5 text-brand-purple" data-id="element-498" data-mp="3lszqt5dok461" />
      {children}
    </div>;
  const labelClass = 'text-xs font-bold text-gray-500 uppercase ml-1';
  const chip = (active: boolean) => `py-2.5 px-4 rounded-full text-sm font-medium border transition-all ${active ? 'bg-brand-purple text-white border-brand-purple shadow-sm shadow-brand-purple/30' : 'bg-white text-gray-600 border-gray-200'}`;
  // --- ENTRY ---
  if (step === 'entry') {
    return <div className="space-y-4 w-full animate-in fade-in duration-300" data-id="element-499" data-mp="1s8pu4ugdps65">
        <div className="text-center mb-6" data-id="element-500" data-mp="1wb2ut4g9qzw2">
          <h2 className="text-3xl font-display text-brand-navy tracking-tight" data-id="element-501" data-mp="1wce1pd50gnre">
            Welcome!
          </h2>
          <p className="text-gray-500 text-sm" data-id="element-502" data-mp="219e7ro1v1qii">
            Is this your first catch or have you caught before?
          </p>
        </div>

        <button onClick={() => {
        setFlow('first');
        setStep('first-basics');
      }} className="w-full bg-brand-purple hover:bg-brand-purple-dark text-white p-6 rounded-[1.75rem] flex flex-col items-center justify-center space-y-3 transition-all shadow-[0_12px_28px_rgba(96,70,232,0.24)]" data-id="element-503" data-mp="18l8mn06w9zi5">
          <UserPlus className="w-8 h-8 text-brand-yellow" data-id="element-504" data-mp="kv5tshv5opih" />
          <span className="font-display text-xl tracking-wide text-white" data-id="element-505" data-mp="puqtiui5h1w9">
            First catch
          </span>
          <span className="text-white/80 text-xs font-medium" data-id="element-506" data-mp="1t54otihvipgm">
            I haven't registered before
          </span>
        </button>

        <button onClick={() => {
        setFlow('returning');
        setStep('returning-id');
      }} className="w-full bg-white border-2 border-brand-purple text-brand-purple hover:bg-brand-lavender p-6 rounded-[1.75rem] flex flex-col items-center justify-center space-y-3 transition-all" data-id="element-507" data-mp="1o6l3nkb3thpb">
          <RefreshCw className="w-8 h-8" data-id="element-508" data-mp="157z3q8rbpwmz" />
          <span className="font-display text-xl tracking-wide" data-id="element-509" data-mp="159aewtn3m6o4">New catch</span>
          <span className="text-brand-purple/70 text-xs font-medium" data-id="element-510" data-mp="1j80csbhqsadj">
            I already have an account
          </span>
        </button>
      </div>;
  }
  // --- CATCH 1: BASICS (new user — only 3 questions) ---
  if (step === 'first-basics') {
    return <form onSubmit={handleFirstBasicsSubmit} className="space-y-5 w-full animate-in slide-in-from-right duration-300" data-id="element-511" data-mp="2pnua12myipuv">
        <div className="text-center" data-id="element-512" data-mp="2y3rsqvmoe0j7">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-513" data-mp="2y6xlqlyfdmk7">A bit about you</h2>
          <p className="text-gray-400 text-sm" data-id="element-514" data-mp="327zld3w980mp">
            Just 3 quick things to set up your first catch
          </p>
        </div>

        <div className="space-y-4" data-id="element-515" data-mp="2t5qd6kogkjch">
          {/* Q1 — name */}
          <div data-id="element-516" data-mp="2t69sdtmq4tmt">
            <label className={labelClass} data-id="element-517" data-mp="2t7ugb0ej9ebp">1. Your name</label>
            <div className="grid grid-cols-2 gap-2 mt-1" data-id="element-518" data-mp="3gwdprj9bjx5d">
              <Input placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} required className="bg-gray-50 border-gray-200" data-id="element-519" data-mp="3lufdihhowkn6" />
              <Input placeholder="Last name" value={lastName} onChange={e => setLastName(e.target.value)} required className="bg-gray-50 border-gray-200" data-id="element-520" data-mp="3c31c2466hare" />
            </div>
          </div>

          {/* Phone — identity so we recognise you next time */}
          <div data-id="element-521" data-mp="2bxqq1q89koex">
            <label className={labelClass} data-id="element-522" data-mp="2bx8wj1czkoap">Phone number</label>
            <Input placeholder="07X XXX XX XX" value={phone} onChange={e => setPhone(e.target.value)} type="tel" required className="bg-gray-50 border-gray-200" data-id="element-523" data-mp="2bzv3ozvyt1se" />
            <p className="text-[11px] text-gray-400 ml-1 mt-1" data-id="element-524" data-mp="2zlrxevtphysr">
              So we can recognise you on your next catch.
            </p>
          </div>

          {/* Q2 — age */}
          <div data-id="element-525" data-mp="2pw89vcv1tgrk">
            <label className={labelClass} data-id="element-526" data-mp="2pvysgdo2khxh">2. Age</label>
            <div className="grid grid-cols-3 gap-2 mt-1" data-id="element-527" data-mp="2kygh6gmkxqmb">
              {ageOptions.map(opt => <button key={opt} type="button" onClick={() => setAgeRange(opt)} className={`py-2.5 px-1 rounded-xl text-sm font-medium border transition-all ${ageRange === opt ? 'bg-brand-purple text-white border-brand-purple' : 'bg-white text-gray-600 border-gray-200'}`} data-id="element-528" data-mp="3idaz8zbzv6t7">
                  {opt}
                </button>)}
            </div>
          </div>

          {/* Q3 — occupation */}
          <div data-id="element-529" data-mp="3plz3uhb3zzu">
            <label className={labelClass} data-id="element-530" data-mp="3q5eb3fkoaa6">3. Occupation</label>
            <div className="grid grid-cols-2 gap-2 mt-1" data-id="element-531" data-mp="3nizqju9kuig">
              {occupationOptions.map(opt => <button key={opt} type="button" onClick={() => setOccupation(opt)} className={`py-2.5 px-1 rounded-xl text-sm font-medium border transition-all ${occupation === opt ? 'bg-brand-purple text-white border-brand-purple' : 'bg-white text-gray-600 border-gray-200'}`} data-id="element-532" data-mp="cqk003wg7re7">
                  {opt}
                </button>)}
            </div>
          </div>

          {/* Optional Partner Code */}
          <div data-id="element-533" data-mp="2shfqh5tqkfk1">
            <label className={labelClass} data-id="element-534" data-mp="2sgw9psftnx7l">Partner Code (Optional)</label>
            <Input placeholder="e.g. EVENT2026" value={partnerCode} onChange={e => setPartnerCode(e.target.value)} className="bg-gray-50 border-gray-200 mt-1" data-id="element-535" data-mp="2nil1fm56durh" />
            <p className="text-[11px] text-gray-400 ml-1 mt-1" data-id="element-536" data-mp="2wj12koyg4pt6">
              If a brand or event invited you, enter their code here.
            </p>
          </div>
        </div>

        <label className="flex items-start space-x-2.5 bg-brand-light rounded-xl p-3 cursor-pointer" data-id="element-537" data-mp="369ccqt8q3v8i">
          <input type="checkbox" checked={notifyOptIn} onChange={e => setNotifyOptIn(e.target.checked)} className="mt-0.5 rounded text-brand-blue w-4 h-4" data-id="element-538" data-mp="3k9dh4io75ksq" />
          <span className="text-xs text-gray-600 leading-relaxed" data-id="element-539" data-mp="1pvvpajgre1rw">
            <span className="font-bold text-gray-800" data-id="element-540" data-mp="1ltikpydr09wp">
              Keep me updated on new drops.
            </span>{' '}
            Get a heads-up the moment a new catch goes live. We'll also point
            you to our Instagram so you never miss one.
          </span>
        </label>

        <Button type="submit" isLoading={isLoading} className="w-full" data-id="element-541" data-mp="11zime0ggx0yk">
          Complete & get your catch
        </Button>

        <button type="button" onClick={() => setStep('entry')} className="w-full text-gray-400 text-xs font-medium mt-2" data-id="element-542" data-mp="16y275xjbs6pc">
          Back
        </button>
      </form>;
  }
  // --- RETURNING: identify the person ---
  if (step === 'returning-id') {
    return <form onSubmit={handleReturningIdSubmit} className="space-y-5 w-full animate-in slide-in-from-right duration-300" data-id="element-543" data-mp="j1eq46loiwe6">
        <div className="text-center" data-id="element-544" data-mp="nzzoe0hdf5zz">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-545" data-mp="2dexkx8obs0vs">Welcome back</h2>
          <p className="text-gray-400 text-sm" data-id="element-546" data-mp="2dcthssy935wk">
            Enter your details to get a new catch
          </p>
        </div>

        <div className="space-y-4" data-id="element-547" data-mp="3qypqhqmqjcn9">
          <div data-id="element-548" data-mp="3qxodvhq2mi3f">
            <label className={labelClass} data-id="element-549" data-mp="3qxepfykocizu">Your name</label>
            <div className="grid grid-cols-2 gap-2 mt-1" data-id="element-550" data-mp="3lyto1vtort9t">
              <Input placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} required className="bg-gray-50 border-gray-200" data-id="element-551" data-mp="3m1futt8r64qz" />
              <Input placeholder="Last name" value={lastName} onChange={e => setLastName(e.target.value)} required className="bg-gray-50 border-gray-200" data-id="element-552" data-mp="388hppf6icya8" />
            </div>
          </div>

          <div data-id="element-553" data-mp="2u8yvzreg1phg">
            <label className={labelClass} data-id="element-554" data-mp="2u98kfajubol1">Phone number</label>
            <Input placeholder="07X XXX XX XX" value={phone} onChange={e => setPhone(e.target.value)} type="tel" required className="bg-gray-50 border-gray-200" data-id="element-555" data-mp="winttt5lqbs4" />
          </div>
        </div>

        {error && <p className="text-red-500 text-xs text-center" data-id="element-556" data-mp="2fcqnmhy8zskv">{error}</p>}

        <Button type="submit" isLoading={isLoading} className="w-full" data-id="element-557" data-mp="2faxf0x42qya8">
          Next <ChevronRight className="w-4 h-4 ml-1" data-id="element-558" data-mp="2k4s8gs9hermi" />
        </Button>

        <button type="button" onClick={() => setStep('entry')} className="w-full text-gray-400 text-xs font-medium mt-2" data-id="element-559" data-mp="2o4iukio2cjkp">
          Back
        </button>
      </form>;
  }
  // --- CATCH 2: deeper interests / profession / where they live ---
  if (step === 'catch2') {
    return <form onSubmit={handleCatch2Submit} className="space-y-5 w-full animate-in slide-in-from-right duration-300" data-id="element-560" data-mp="20eg7kgz0a3sy">
        <div className="text-center" data-id="element-561" data-mp="3bv6gi7utd9q9">
          <CatchBadge data-id="element-562" data-mp="3bu4vbak336ut">
            {recognized ? `Welcome back, ${firstName}` : 'Your 2nd catch'}
          </CatchBadge>
          <h2 className="text-lg font-bold text-gray-900" data-id="element-563" data-mp="3gsoitfebsqpa">
            Help us match better drops
          </h2>
          <p className="text-gray-400 text-sm" data-id="element-564" data-mp="3i8w9t5ltiqvh">
            3 quick questions now that you're a regular
          </p>
        </div>

        {/* Q1 — interests (up to 3) */}
        <div data-id="element-565" data-mp="3s239qaxsheo6">
          <label className={`${labelClass} mb-2 block`} data-id="element-566" data-mp="3s1k33qdlacpg">
            1. What do you want more of?{' '}
            <span className="text-gray-400 normal-case font-medium" data-id="element-567" data-mp="tpjfwzgaot6">
              (up to 3)
            </span>
          </label>
          <div className="flex flex-wrap gap-2" data-id="element-568" data-mp="34b7k9kgt2vk3">
            {interestOptions.map(opt => <button key={opt.label} type="button" onClick={() => toggleInterest(opt.label)} className={`${chip(interests.includes(opt.label))} flex items-center space-x-1.5`} data-id="element-569" data-mp="39aajgoa3u7k5">
                <span data-id="element-570" data-mp="ojjqfxx39j5i">{opt.emoji}</span>
                <span data-id="element-571" data-mp="tj4pe8c9dri8">{opt.label}</span>
              </button>)}
          </div>
          {interests.length > 0 && <p className="text-xs text-brand-blue font-medium mt-2 ml-1" data-id="element-572" data-mp="2e1goe4e28xi4">
              {interests.length}/3 selected
            </p>}
        </div>

        {/* Q2 — exact profession / studies */}
        <div data-id="element-573" data-mp="24acal804fiov">
          <label className={`${labelClass} mb-1 block`} data-id="element-574" data-mp="249t2ej09w8o1">
            2. Your exact profession or studies
          </label>
          <Input placeholder={occupation === 'Student' ? 'e.g. Computer Science @ KTH' : 'e.g. UX Designer at Spotify'} value={profession} onChange={e => setProfession(e.target.value)} required className="bg-gray-50 border-gray-200" data-id="element-575" data-mp="1za6nm54sqkd4" />
          {occupation && <p className="text-[11px] text-gray-400 ml-1 mt-1" data-id="element-576" data-mp="1gfjbqnmd3me3">
              You told us you're “{occupation}” — tell us a little more.
            </p>}
        </div>

        {/* Q3 — where they live */}
        <div data-id="element-577" data-mp="3apn0tdh84u6y">
          <label className={`${labelClass} mb-1 block`} data-id="element-578" data-mp="192k3tua3srmi">
            3. Where do you live?{' '}
            <span className="text-gray-400 normal-case font-medium" data-id="element-579" data-mp="1dsqpbl9bjw0j">
              (postcode or city)
            </span>
          </label>
          <Input placeholder="e.g. 118 50 or Stockholm" value={city} onChange={e => setCity(e.target.value)} required className="bg-gray-50 border-gray-200" data-id="element-580" data-mp="z5fejlkyog1h" />
        </div>

        <Button type="submit" isLoading={isLoading} className="w-full" data-id="element-581" data-mp="1rqxhqlvi13st">
          Complete & get your catch
        </Button>
      </form>;
  }
  // --- CATCH 3: work area / favourite area / pickup distributors ---
  if (step === 'catch3') {
    return <form onSubmit={handleCatch3Submit} className="space-y-5 w-full animate-in slide-in-from-right duration-300" data-id="element-582" data-mp="1msdqq721t1tl">
        <div className="text-center" data-id="element-583" data-mp="6hrxggiwg7jl">
          <CatchBadge data-id="element-584" data-mp="bes2co40j494">
            {recognized ? `Great to see you, ${firstName}` : 'Your 3rd catch'}
          </CatchBadge>
          <h2 className="text-lg font-bold text-gray-900" data-id="element-585" data-mp="2pkpzrv5w1spf">
            A few last details
          </h2>
          <p className="text-gray-400 text-sm" data-id="element-586" data-mp="2kmoldnjeqhka">
            This helps us drop catches closer to you
          </p>
        </div>

        {/* Q1 — where they work (by area) */}
        <div data-id="element-587" data-mp="2umffvp7ywwkd">
          <label className={`${labelClass} mb-1 block`} data-id="element-588" data-mp="2ulvv80qxnu2n">
            <Briefcase className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-brand-blue" data-id="element-589" data-mp="26v9yv6qajy8h" />
            1. Where do you work?{' '}
            <span className="text-gray-400 normal-case font-medium" data-id="element-590" data-mp="21xrm1595kyv7">
              (by area)
            </span>
          </label>
          <Input placeholder="e.g. Kista, City, Solna" value={workArea} onChange={e => setWorkArea(e.target.value)} required className="bg-gray-50 border-gray-200" data-id="element-591" data-mp="2fpem7g67urj9" />
        </div>

        {/* Q2 — favourite area to be */}
        <div data-id="element-592" data-mp="1j5yttd1wrhoj">
          <label className={`${labelClass} mb-2 block`} data-id="element-593" data-mp="1j44j0rkcp5y6">
            <HomeIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-brand-blue" data-id="element-594" data-mp="1vwhk5dlqrter" />
            2. Where do you usually like to be?
          </label>
          <div className="flex flex-wrap gap-2" data-id="element-595" data-mp="20s6n7qwyzsft">
            {areaOptions.map(opt => <button key={opt} type="button" onClick={() => setFavoriteArea(opt)} className={chip(favoriteArea === opt)} data-id="element-596" data-mp="25rjd6kj51fmm">
                {opt}
              </button>)}
          </div>
        </div>

        {/* Q3 — preferred pickup distributor */}
        <div data-id="element-597" data-mp="2ojls9rg4snur">
          <label className={`${labelClass} mb-2 block`} data-id="element-598" data-mp="2ol5dzxkk3r3x">
            <MapPin className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-brand-blue" data-id="element-599" data-mp="2tj6ss6ay949l" />
            3. Preferred pickup spot
          </label>
          <select value={pickupPref} onChange={e => setPickupPref(e.target.value)} className="w-full p-3 rounded-xl bg-gray-50 border-2 border-gray-200 focus:border-brand-blue outline-none text-gray-700" required data-id="element-600" data-mp="u74erbl3h23b">
            <option value="" disabled data-id="element-601" data-mp="mv7t2cv7rllb">
              Choose pickup location...
            </option>
            {pickupOptions.map(opt => <option key={opt} value={opt} data-id="element-602" data-mp="hwd22nn3eqq4">
                {opt}
              </option>)}
          </select>
        </div>

        {/* Q4 — where they live (asked after pickup preference on the 3rd catch) */}
        <div data-id="element-603" data-mp="1aifyqvwk0wnv">
          <label className={`${labelClass} mb-1 block`} data-id="element-604" data-mp="15lftuobfxzyc">
            <HomeIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-brand-blue" data-id="element-605" data-mp="15lz1nc7dn7yn" />
            4. Where do you live?{' '}
            <span className="text-gray-400 normal-case font-medium" data-id="element-606" data-mp="10ofm88yy0p54">
              (postcode or city)
            </span>
          </label>
          <Input placeholder="e.g. 118 50 or Stockholm" value={city} onChange={e => setCity(e.target.value)} required className="bg-gray-50 border-gray-200" data-id="element-607" data-mp="3ld3f3mh3e41b" />
        </div>

        <Button type="submit" isLoading={isLoading} className="w-full" data-id="element-608" data-mp="28etf4k819jdh">
          Complete & get your catch
        </Button>
      </form>;
  }
  // --- CATCH 4+: Recurring & Drop Questions ---
  if (step === 'catch-recurring') {
    const activeEvent = events.find(e => e.id === activeEventId);
    const dropQs = activeEvent?.customQuestions || [];
    const recQs = recurringQuestions || [];
    const allQs = [...recQs, ...dropQs];
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      completeCatch(recognized || undefined);
    };
    return <form onSubmit={handleSubmit} className="space-y-5 w-full animate-in slide-in-from-right duration-300" data-id="element-609" data-mp="1fqfj2a0i8x4k">
        <div className="text-center" data-id="element-610" data-mp="1kmcx29oapre6">
          <CatchBadge data-id="element-611" data-mp="15qolfzgne209">
            {recognized ? `Welcome back, ${firstName}` : 'Catch Secured'}
          </CatchBadge>
          <h2 className="text-lg font-bold text-gray-900" data-id="element-612" data-mp="1ap89c5esxnv9">
            A few quick questions
          </h2>
          <p className="text-gray-400 text-sm" data-id="element-613" data-mp="r59btzj3hqpf">
            Help us make the next drop even better
          </p>
        </div>

        {allQs.map((q, i) => <div key={q.id} data-id="element-614" data-mp="112e2ocnvyack" data-mpi={i}>
            <label className={`${labelClass} mb-2 block`} data-id="element-615" data-mp="113p3c3m1b1zg">
              {i + 1}. {q.label}
            </label>
            {q.type === 'text' ? <Input required placeholder="Your answer..." value={customAnswers[q.id] || ''} onChange={e => setCustomAnswers({
          ...customAnswers,
          [q.id]: e.target.value
        })} className="bg-gray-50 border-gray-200" data-id="element-616" data-mp="w5xe5hcrxtyy" /> : <div className="flex flex-wrap gap-2" data-id="element-617" data-mp="1tr34kdwrmjpd">
                {(q.options || []).map(opt => <button key={opt} type="button" onClick={() => setCustomAnswers({
            ...customAnswers,
            [q.id]: opt
          })} className={chip(customAnswers[q.id] === opt)} data-id="element-618" data-mp="1osrv44adub7o">
                    {opt}
                  </button>)}
              </div>}
          </div>)}

        <Button type="submit" isLoading={isLoading} className="w-full" disabled={allQs.some(q => !customAnswers[q.id])} data-id="element-619" data-mp="3mvlhyouciqlf">
          Complete & get your catch
        </Button>
      </form>;
  }
  // --- DONE ---
  if (step === 'done') {
    const isFirstCatch = catchNumber < 2;
    // First-time onboarding: three slides shown ONCE, after the first catch.
    const onboardingSlides = [{
      icon: Package,
      accent: 'bg-brand-blue/10 text-brand-blue',
      title: 'Open & test your catch',
      body: 'Your box is packed with real products from real brands. Open it up and try everything inside.'
    }, {
      icon: Star,
      accent: 'bg-brand-yellow/20 text-brand-blue',
      title: 'Review to unlock the next drop',
      body: 'Leave a quick, honest review in the app. It only takes a minute and unlocks your next catch.'
    }, {
      icon: Bell,
      accent: 'bg-brand-blue/20 text-brand-blue',
      title: 'Never miss a drop',
      body: "We'll notify you the moment a new catch goes live — and following us on Instagram keeps you first in line."
    }];
    if (isFirstCatch && onboardingSlide < onboardingSlides.length) {
      const slide = onboardingSlides[onboardingSlide];
      const Icon = slide.icon;
      const isLast = onboardingSlide === onboardingSlides.length - 1;
      return <div className="w-full animate-in fade-in slide-in-from-right duration-300 text-center flex flex-col items-center py-4" role="group" aria-label={`Getting started — step ${onboardingSlide + 1} of ${onboardingSlides.length}`} data-id="element-620" data-mp="ohmchbhzmutn">
          <div className="w-24 h-24 bg-brand-yellow rounded-full flex items-center justify-center mx-auto shadow-[0_0_40px_rgba(255,204,0,0.6)] mb-6" data-id="element-621" data-mp="3kmn5b09obyte">
            <span className="text-5xl" data-id="element-622" data-mp="2e4ommp8svwb">🎣</span>
          </div>
          <p className="text-xs font-bold text-brand-blue uppercase tracking-widest mb-1" data-id="element-623" data-mp="3tntoxi7mdpqv">
            You're in the crew
          </p>
          <h2 className="text-3xl font-display text-brand-blue uppercase tracking-wide mb-8" data-id="element-624" data-mp="35uv37pyw4h9m">
            Catch secured!
          </h2>

          <div key={onboardingSlide} className="w-full bg-white rounded-2xl p-7 shadow-lg border-2 border-brand-blue/10 mb-6 animate-in fade-in zoom-in-95 duration-300" data-id="element-625" data-mp="3fnakel2d51tg">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 ${slide.accent}`} data-id="element-626" data-mp="2n5offoj8ozgy">
              <Icon className="w-8 h-8" data-id="element-627" data-mp="2i73f7p3zmfsi" />
            </div>
            <h3 className="font-bold text-gray-900 text-xl mb-2" data-id="element-628" data-mp="xp2jpdg2ektc">
              {slide.title}
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed" data-id="element-629" data-mp="12h2phmitg7xu">
              {slide.body}
            </p>
          </div>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 mb-6" data-id="element-630" data-mp="tnqv9b8xg5mq">
            {onboardingSlides.map((_, i) => <span key={i} className={`h-2 rounded-full transition-all ${i === onboardingSlide ? 'w-6 bg-brand-blue' : 'w-2 bg-gray-300'}`} data-id="element-631" data-mp="1hcs42zlo9isj" data-mpi={i} />)}
          </div>

          <Button onClick={() => setOnboardingSlide(s => s + 1)} className="w-full" data-id="element-632" data-mp="17e2qhj0t6ki3">
            {isLast ? "Let's go!" : 'Next'}
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-633" data-mp="3s5mlyy5r3c92" />
          </Button>
          {!isLast && <button type="button" onClick={() => setOnboardingSlide(onboardingSlides.length)} className="w-full text-gray-400 text-xs font-medium mt-3" data-id="element-634" data-mp="ysas7hc0re9">
              Skip
            </button>}
        </div>;
    }
    // Success screen — what returning users see immediately, and what
    // first-timers land on after finishing the onboarding slides.
    return <div className="space-y-8 w-full animate-in zoom-in duration-500 text-center flex flex-col items-center justify-center py-4" data-id="element-635" data-mp="2z9vhjua0pugj">
        <div className="w-24 h-24 bg-brand-yellow rounded-full flex items-center justify-center mx-auto shadow-[0_0_40px_rgba(255,204,0,0.6)] animate-bounce" data-id="element-636" data-mp="2u9r0o3576xps">
          <span className="text-5xl" data-id="element-637" data-mp="2lc6xgonryrcj">🎣</span>
        </div>

        <div data-id="element-638" data-mp="2gc2ij32im4of">
          <h2 className="text-4xl font-display text-brand-blue uppercase tracking-wide mb-2" data-id="element-639" data-mp="2gcm02io96r1x">
            CATCH SECURED!
          </h2>
          <p className="text-gray-600 font-medium" data-id="element-640" data-mp="jihdbu7fon74">
            {catchNumber >= 2 ? `Catch #${catchNumber} — thanks for coming back!` : "You're officially in the crew."}
          </p>
        </div>

        {/* Follow us — keep in touch + earn a one-time reward */}
        <InstagramFollow className="w-full" data-id="element-641" data-mp="r20gzn80pf7o" />

        {/* Next drop — date + save-to-calendar (inviting friends now lives in
                    the Friends tab, unlocked after a first redemption + review). */}
        {nextDrop && <div className="bg-brand-blue rounded-2xl p-5 text-left shadow-xl w-full border-4 border-brand-blue/20" data-id="element-642" data-mp="10uxsh9ele3ws">
            <p className="text-xs font-bold text-brand-yellow uppercase tracking-widest mb-2" data-id="element-643" data-mp="84ftokkwiop6">
              Next drop
            </p>
            <div className="flex items-start space-x-3 mb-4" data-id="element-644" data-mp="d2h9mwn167wf">
              {nextDrop.brandLogo && <div className="w-12 h-12 rounded-lg bg-white border border-brand-blue/30 flex items-center justify-center p-1 shrink-0" data-id="element-645" data-mp="h66zdpffyvj1">
                  <img src={nextDrop.brandLogo} alt={nextDrop.brand} className="max-w-full max-h-full object-contain mix-blend-multiply" data-id="element-646" data-mp="1oks79wih22jo" />
                </div>}
              <div data-id="element-647" data-mp="1m53tor97afru">
                <h3 className="font-display text-2xl text-white tracking-wide mb-1 leading-tight" data-id="element-648" data-mp="1m5ln7g4hafw2">
                  {nextDrop.title}
                </h3>
                <p className="text-sm text-brand-light/80" data-id="element-649" data-mp="1w1oypcl7zqvo">{nextDrop.brand}</p>
              </div>
            </div>

            {(nextDrop.instagramUrl || nextDrop.websiteUrl) && <div className="flex space-x-2 mb-4" data-id="element-650" data-mp="252wikpmzop68">
                {nextDrop.instagramUrl && <a href={nextDrop.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-pink-600 bg-pink-50 px-2 py-1 rounded-md hover:bg-pink-100 transition-colors" data-id="element-651" data-mp="2jrt0deeulgk1">
                    Instagram
                  </a>}
                {nextDrop.websiteUrl && <a href={nextDrop.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-md hover:bg-blue-100 transition-colors" data-id="element-652" data-mp="37eo9us5labku">
                    Website
                  </a>}
              </div>}

            <div className="space-y-2 text-sm text-white/90 font-medium mb-4" data-id="element-653" data-mp="2oo6hwpskzk0j">
              <div className="flex items-center" data-id="element-654" data-mp="2tfetnshdvhgv">
                <Calendar className="w-4 h-4 mr-2 text-brand-yellow shrink-0" data-id="element-655" data-mp="2tg6mivjlj13g" />
                <span data-id="element-656" data-mp="2fotae2o0p5ig">{nextDrop.date}</span>
              </div>
              <div className="flex items-center" data-id="element-657" data-mp="2jpli2rh8r2cm">
                <Clock className="w-4 h-4 mr-2 text-brand-yellow shrink-0" data-id="element-658" data-mp="2jqeua7yyyk25" />
                <span data-id="element-659" data-mp="25pkg18p8povk">{nextDrop.time}</span>
              </div>
              <a href={mapsUrl(nextDrop.address)} target="_blank" rel="noopener noreferrer" className="flex items-center text-brand-yellow hover:text-white hover:underline" data-id="element-660" data-mp="25s6pxezlsggy">
                <MapPin className="w-4 h-4 mr-2 shrink-0" data-id="element-661" data-mp="bh6wuvy7hkm1" />
                <span data-id="element-662" data-mp="7g56l4m9oiw7">{nextDrop.address}</span>
              </a>
            </div>

            {/* Save the reserved drop to your calendar */}
            <div className="pt-3 border-t border-white/15" data-id="element-663" data-mp="2ilf330k69mb">
              <p className="text-[11px] font-bold text-brand-yellow uppercase tracking-widest mb-2" data-id="element-664" data-mp="z7b2f8uyfufo">
                Save the date
              </p>
              <div className="grid grid-cols-3 gap-2" data-id="element-665" data-mp="v6heifhiifms">
                <a href={googleCalendarUrl(nextDrop)} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center gap-1 bg-white/10 hover:bg-white/20 text-white rounded-xl py-2.5 px-1 text-[11px] font-bold transition-colors" data-id="element-666" data-mp="q6d3h4zdid3y">
                  <CalendarPlus className="w-4 h-4" data-id="element-667" data-mp="35wplc4fhrbgc" />
                  Google
                </a>
                <button type="button" onClick={() => downloadIcs(nextDrop)} className="flex flex-col items-center justify-center gap-1 bg-white/10 hover:bg-white/20 text-white rounded-xl py-2.5 px-1 text-[11px] font-bold transition-colors" data-id="element-668" data-mp="2vvx0ken3uotd">
                  <Download className="w-4 h-4" data-id="element-669" data-mp="3akt6o65lprrc" />
                  .ics file
                </button>
                <button type="button" onClick={handleCopyDetails} className="flex flex-col items-center justify-center gap-1 bg-white/10 hover:bg-white/20 text-white rounded-xl py-2.5 px-1 text-[11px] font-bold transition-colors" data-id="element-670" data-mp="3emxwsuwuc698">
                  {detailsCopied ? <Check className="w-4 h-4 text-brand-yellow" data-id="element-671" data-mp="1u5i8faw2xmkd" /> : <Copy className="w-4 h-4" data-id="element-672" data-mp="1z3bdu1pk6cjl" />}
                  {detailsCopied ? 'Copied' : 'Copy info'}
                </button>
              </div>
            </div>
          </div>}

        <Button onClick={() => onSuccess && onSuccess()} className="w-full" data-id="element-673" data-mp="3w18whnbczd2g">
          {isFirstCatch ? 'Explore the app' : "LET'S GO!"}
        </Button>
      </div>;
  }
  return null;
}